import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MapPin, Briefcase, IndianRupee, Clock, Building2, Bookmark, CheckCircle2, Calendar, Users } from "lucide-react";

export default function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => toast.error("Job not found")).finally(() => setLoading(false));
  }, [id]);

  const apply = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "candidate") { toast.error("Only candidates can apply"); return; }
    try {
      await api.post(`/jobs/${id}/apply`);
      setJob((j) => ({ ...j, applied: true }));
      toast.success("Application submitted!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };

  const toggleSave = async () => {
    if (!user) { navigate("/login"); return; }
    const r = await api.post(`/jobs/${id}/save`);
    setJob((j) => ({ ...j, saved: r.data.saved }));
  };

  if (loading) return <PublicLayout><div className="max-w-4xl mx-auto px-6 py-20"><div className="h-64 bg-white rounded-xl animate-pulse" /></div></PublicLayout>;
  if (!job) return <PublicLayout><div className="max-w-4xl mx-auto px-6 py-20 text-center text-slate-500">Job not found.</div></PublicLayout>;

  const Section = ({ title, text }) => text ? (
    <div className="mb-6">
      <h3 className="font-display font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{text}</p>
    </div>
  ) : null;

  return (
    <PublicLayout>
      <div className="bg-slate-900 hero-grid">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <button onClick={() => navigate(-1)} className="text-slate-400 text-sm hover:text-white mb-4">← Back to jobs</button>
          <h1 className="font-display text-3xl font-bold text-white">{job.title}</h1>
          <div className="flex items-center gap-2 text-slate-300 mt-2"><Building2 className="w-4 h-4" />{job.company_name}</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#D96B27]" />{job.location}</span>
            <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-[#D96B27]" />{job.min_experience}-{job.max_experience} yrs</span>
            {job.salary_min && <span className="flex items-center gap-1.5"><IndianRupee className="w-4 h-4 text-[#D96B27]" />{job.salary_min}-{job.salary_max} LPA</span>}
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#D96B27]" />{job.employment_type}</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#D96B27]" />{job.vacancies} vacancies</span>
          </div>
          <div className="flex gap-3 mt-6">
            <button data-testid="job-apply-btn" onClick={apply} disabled={job.applied} className="copper-gradient text-white font-semibold px-6 py-2.5 rounded-lg disabled:opacity-60">
              {job.applied ? "Applied ✓" : "Apply Now"}
            </button>
            <button data-testid="job-detail-save-btn" onClick={toggleSave} className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-5 py-2.5 rounded-lg hover:bg-white/20">
              <Bookmark className={`w-4 h-4 ${job.saved ? "fill-[#D96B27] text-[#D96B27]" : ""}`} /> {job.saved ? "Saved" : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_300px] gap-8">
        <div className="bg-white rounded-xl border border-slate-200 p-7">
          <Section title="Job Description" text={job.description} />
          <Section title="Key Responsibilities" text={job.responsibilities} />
          <Section title="Required Qualifications" text={job.required_qualifications} />
          <Section title="Preferred Qualifications" text={job.preferred_qualifications} />
        </div>
        <aside className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-display font-semibold text-slate-900 mb-3 text-sm">Job Overview</h3>
            <dl className="space-y-2.5 text-sm">
              {[["Department", job.department], ["Designation", job.designation], ["Industry", job.industry], ["Plant Location", job.plant_location], ["Shift", job.shift], ["Cable Exp.", `${job.cable_experience}+ yrs`]].map(([k, v]) => v ? (
                <div key={k} className="flex justify-between gap-2"><dt className="text-slate-400">{k}</dt><dd className="text-slate-700 font-medium text-right">{v}</dd></div>
              ) : null)}
              {job.deadline && <div className="flex justify-between gap-2"><dt className="text-slate-400 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Deadline</dt><dd className="text-slate-700 font-medium">{job.deadline.slice(0, 10)}</dd></div>}
            </dl>
          </div>
          {(job.products?.length > 0) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-3 text-sm">Products & Skills</h3>
              <div className="flex flex-wrap gap-1.5">
                {job.products.map((p) => <span key={p} className="text-xs bg-[#FFF3EC] text-[#C05615] px-2 py-0.5 rounded">{p}</span>)}
                {(job.processes || []).map((p) => <span key={p} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{p}</span>)}
                {(job.skills || []).map((p) => <span key={p} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{p}</span>)}
              </div>
            </div>
          )}
        </aside>
      </div>
    </PublicLayout>
  );
}
